import React, { useEffect, useMemo, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { NoData } from "src/shared/components/tables/no-data";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TablePagination,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useRecoilState } from "recoil";
import CustomAutocomplete from "src/shared/components/custom-auto-complete";
import {
  useExportEmployeesShifts,
  useGetEmployeesShiftsList,
} from "src/api/employees";
import { useGetLocationsList } from "src/api/locations";
import CustomDatePicker from "src/shared/components/custom-date-picker";
import moment from "moment";
import { downloadFile } from "src/utils";
import { LoadingButton } from "@mui/lab";
import { notificationsState } from "src/store/notifications";
import NoFilterSelected from "src/shared/components/no-filters-selected";
import { ICity } from "src/types/generic";
import { useGetCitiesList } from "src/api/general";
import TableActions from "./table-actions";

const tableHeadTexts = [
  "Employee Name",
  "Location",
  "Date",
  "Start Shift at",
  "End Shift at",
  "Total Hours",
  "Late Time",
  "Overtime",
  "Approved By",
  "Actions",
];

const ActiveLocationsTable = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(10);
  const [tempSearchBy, setTempSearchBy] = useState<string>("");
  const [searchBy, setSearchBy] = useState<string>("");
  const [selectedLocation, setSelectedLocation] = useState<any>();
  const [tempSelectedLocation, setTempSelectedLocation] = useState<any>();
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(null);
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [tempSelectedCity, setTempSelectedCity] = useState<ICity>();
  const [selectedCity, setSelectedCity] = useState<ICity>();

  const filters = {
    ...(!!searchBy ? { search: searchBy } : {}),
    ...(selectedLocation && { location: selectedLocation.id }),
    ...(selectedCity && { cityId: selectedCity.id }),
    ...(startDate
      ? { startDate: moment(startDate).startOf("day").valueOf() }
      : {}),
    ...(endDate ? { endDate: moment(endDate).endOf("day").valueOf() } : {}),
  };

  // APIS
  const { data, isFetching, isLoading } = useGetEmployeesShiftsList({
    page: page + 1,
    limit: rowsPerPage,
    ...filters,
  });
  const { data: locationsData } = useGetLocationsList();
  const { isFetching: isFetchingExport, refetch: refetchExport } =
    useExportEmployeesShifts(filters);
  const { data: citiesData } = useGetCitiesList({
    type: "active",
  });

  const shiftsList = data?.data?.data || [];

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeCity = (event: React.SyntheticEvent, newValue: any) => {
    setTempSelectedCity(newValue);
  };

  const handleSearch = (searchedVal: string) => {
    setPage(0);
    setTempSearchBy(searchedVal);
  };

  const clearSearch = () => {
    setTempSearchBy("");
    if (searchBy !== "") {
      setPage(0);
    }
  };

  //Filters
  const handleChangeLocation = (event: React.SyntheticEvent, newValue: any) => {
    setTempSelectedLocation(newValue);
  };

  const handleApply = () => {
    setSearchBy(tempSearchBy);
    setSelectedLocation(tempSelectedLocation);
    setSelectedCity(tempSelectedCity);
    setSearchBy(tempSearchBy);
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
  };

  const handleResetFilers = () => {
    setPage(0);

    setSelectedLocation(undefined);
    setSelectedCity(undefined);
    setSearchBy("");
    setStartDate(null);
    setEndDate(null);

    setTempSelectedLocation(undefined);
    setTempSelectedCity(undefined);
    setTempSearchBy("");
    setTempStartDate(null);
    setTempEndDate(null);
  };

  const handleExportEmployeesShifts = () => {
    refetchExport().then((res) => {
      if (res.status === "success" && res.data) {
        const utf8CSVData = `\uFEFF${res.data}`;
        downloadFile(utf8CSVData, "text/csv", "employees-shifts");
      }
    });
  };

  const emptyDataArr = data?.data?.data?.length === 0 && !isFetching;

  useEffect(() => {
    setTotalCount(data?.data?.total ?? 0);
  }, [data]);

  const sortedLocations = useMemo(() => {
    return (
      locationsData?.locations.sort((a: any, b: any) =>
        a.nameEn?.localeCompare(b.nameEn),
      ) ?? []
    );
  }, [locationsData]);

  const filteredCities = useMemo(() => {
    if (!citiesData?.data) return [];

    if (!tempSelectedLocation) {
      return citiesData.data;
    }

    const availableLocations = tempSelectedLocation
      ? [tempSelectedLocation]
      : sortedLocations;

    const availableLocationsCitiesIds = availableLocations.map(
      (loc: any) => loc.cityId,
    );

    return citiesData.data.filter((city) =>
      availableLocationsCitiesIds.includes(city.id),
    );
  }, [tempSelectedLocation, citiesData, sortedLocations]);

  useEffect(() => {
    console.log({ startDate, endDate });
    if (startDate && endDate && startDate > endDate) {
      setNotifications((prev) => [
        ...prev,
        {
          type: "error",
          message: "Start date can't be after end date!",
        },
      ]);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    setTempSelectedCity(undefined);
  }, [tempSelectedLocation]);

  return (
    <>
      <Stack direction={{ xs: "column", md: "row" }} gap={"10px"} mb={2}>
        <Stack direction={"row"} gap={"10px"} flexWrap={"wrap"} flexGrow={1}>
          <TextField
            data-testid="shifts-search-input"
            size="small"
            placeholder="Search"
            sx={{ width: { xs: "100%", sm: "250px" } }}
            value={tempSearchBy}
            onChange={(event) => handleSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{ opacity: !tempSearchBy ? 0 : 1 }}
                >
                  <IconButton size="small" onClick={clearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box width={"200px"}>
            <CustomAutocomplete
              data-testid="shifts-location-input"
              size="small"
              fullWidth
              disablePortal
              id="combo-box-demo"
              key={tempSelectedLocation?.id ?? "location-autocomplete"}
              options={sortedLocations}
              getOptionLabel={(option: any) => option.nameEn}
              getOptionKey={(option: any) => option.id}
              value={tempSelectedLocation}
              onChange={handleChangeLocation}
              renderInput={(params: any) => (
                <TextField fullWidth {...params} label={"Location"} />
              )}
            />
          </Box>
          {/* city */}
          <Box width={"170px"}>
            <CustomAutocomplete
              key={tempSelectedCity?.id ?? "city-autocomplete"}
              size="small"
              disablePortal
              id="combo-box-demo"
              options={filteredCities}
              getOptionLabel={(option) => option.nameEn}
              getOptionKey={(option) => `${option.id}`}
              value={tempSelectedCity}
              onChange={handleChangeCity}
              renderInput={(params: any) => (
                <TextField fullWidth {...params} label={"City"} />
              )}
            />
          </Box>
          {/* start date */}
          <CustomDatePicker
            data-testid="shifts-start-input"
            label="Start Date"
            slotProps={{
              textField: {
                size: "small",
                sx: {
                  width: "126px",
                },
                inputProps: { readOnly: true },
              },
            }}
            format={"dd/MM/yyyy"}
            value={tempStartDate}
            onChange={(newValue) => setTempStartDate(newValue)}
            monthsPerRow={4}
            name="start date"
            disableOpenPicker
            maxDate={moment().toDate()}
          />
          {/* end date */}
          <CustomDatePicker
            data-testid="shifts-end-input"
            label="End Date"
            slotProps={{
              textField: {
                size: "small",
                sx: {
                  width: "126px",
                },
                inputProps: { readOnly: true },
              },
            }}
            format={"dd/MM/yyyy"}
            value={tempEndDate}
            onChange={(newValue) => setTempEndDate(newValue)}
            monthsPerRow={4}
            name="end_date"
            disableOpenPicker
            maxDate={moment().toDate()}
          />
        </Stack>
        <Stack direction={"row"} gap={0.5}>
          <Button
            onClick={handleResetFilers}
            data-testid="shifts-clear-filters-button"
          >
            Clear
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            sx={{ width: "100px" }}
            data-testid="shifts-apply-filters-button"
          >
            Apply
          </Button>
        </Stack>
      </Stack>
      {!selectedLocation && !searchBy && !selectedCity ? (
        <NoFilterSelected
          message={
            "Search for an employee or select a location or city to see results."
          }
        />
      ) : (
        <>
          <Stack alignItems={"flex-end"} mb={2.2}>
            {shiftsList?.length > 0 ? (
              <LoadingButton
                data-testid="shifts-export-button"
                loading={isFetchingExport}
                variant="contained"
                onClick={handleExportEmployeesShifts}
              >
                Export
              </LoadingButton>
            ) : null}
          </Stack>

          <TableContainer
            sx={{
              bgcolor: "#1A1A1A",
              boxShadow: "0px 14px 44px 0px rgba(0, 0, 0, 0.35)",
              padding: "10px 30px",
            }}
          >
            <Table
              data-testid="shifts-table"
              sx={{ minWidth: 650 }}
              aria-label="simple table"
            >
              <TableHead>
                <TableRow>
                  {tableHeadTexts.map((item) => (
                    <TableCell
                      key={item}
                      sx={{
                        fontWeight: 600,
                        color: "#A1A5B7",
                      }}
                    >
                      {item}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              {isLoading ? (
                <TableLoadingSkeleton
                  rowsLength={rowsPerPage}
                  cellsLength={10}
                />
              ) : (
                <TableBody>
                  {shiftsList.map((row: any) => (
                    <TableRow
                      key={row.id}
                      sx={{
                        cursor: "pointer",
                        "&:last-child td, &:last-child th": {
                          border: 0,
                        },
                      }}
                    >
                      <TableCell>{row.employeeName || "-"}</TableCell>
                      <TableCell>{row.location || "-"}</TableCell>
                      <TableCell sx={{ minWidth: "103px" }}>
                        {row.date || "-"}
                      </TableCell>
                      <TableCell>
                        {row.startShiftAt
                          ? moment(row.startShiftAt).format("hh:mm A")
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {row.endShiftAt
                          ? moment(row.endShiftAt).format("hh:mm A")
                          : "-"}
                      </TableCell>
                      <TableCell>{row.totalHours ?? "-"}</TableCell>
                      <TableCell>{row.lateHours ?? "-"}</TableCell>
                      <TableCell>{row.overtimeHours ?? "-"}</TableCell>
                      <TableCell>{row.approvedBy || "-"}</TableCell>
                      <TableCell>
                        <TableActions row={row} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>

            {emptyDataArr && <NoData />}

            <TablePagination
              data-testid="shifts-table-pagination"
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </>
      )}
    </>
  );
};
export default ActiveLocationsTable;
